// Zugriff auf /api/notierungen - der Bereich Rohstoffnotierungen. Eine Funktion je Endpunkt, dieselbe
// Fehler- und Ladebehandlung wie api.ts (hole, mitSuche, loesche, schickeFormular kommen von dort).

import { hole, loesche, mitSuche, schickeFormular } from './api'
import type {
  NotierungEinstellungen,
  NotierungExcelAntwort,
  NotierungGruppe,
  NotierungLauf,
  NotierungLaufKurz,
  NotierungLaufStatus,
  NotierungLieferant,
  NotierungQuelle,
  NotierungQuelleEingabe,
  NotierungRegel,
  NotierungRegelAntwort,
  NotierungRegelEingabe,
  NotierungRohstoff,
  NotierungStatus,
  NotierungTabelle,
  NotierungVerlauf,
  NotierungenHeute,
} from './typen'

const P = '/api/notierungen'

const json = (methode: string, koerper: unknown): RequestInit => ({ method: methode, body: JSON.stringify(koerper) })

export const notierungenApi = {
  // --- Heute, Tabelle, Verlauf ------------------------------------------------------------------
  heute: (am?: string) => hole<NotierungenHeute>(mitSuche(`${P}/heute`, { am })),
  gruppen: () => hole<NotierungGruppe[]>(`${P}/gruppen`),
  /** Das Excel-Blatt einer Gruppe zum Stichtag; ohne `am` der letzte Tag mit Notierungen. */
  tabelle: (gruppe: string, am?: string) => hole<NotierungTabelle>(mitSuche(`${P}/tabelle`, { gruppe, am })),
  /** Preis je Lieferant über die Notierungstage für einen Lieferzeitraum (2026-11, 2026-Q4, 2026-H2, 2026). */
  verlauf: (p: { rohstoff_id: number; zeitraum?: string; von?: string; bis?: string; inkl_transport: boolean }) =>
    hole<NotierungVerlauf>(
      mitSuche(`${P}/verlauf`, {
        rohstoff_id: p.rohstoff_id,
        zeitraum: p.zeitraum,
        von: p.von,
        bis: p.bis,
        inkl_transport: String(p.inkl_transport),
      }),
    ),

  // --- Läufe (Import) ---------------------------------------------------------------------------
  laeufe: (status?: NotierungLaufStatus) => hole<NotierungLaufKurz[]>(mitSuche(`${P}/laeufe`, { status })),
  lauf: (id: number) => hole<NotierungLauf>(`${P}/laeufe/${id}`),
  /** Bis zu zehn Dateien (PDF, Bild, .eml, .txt) und optional eingefügter Text - ein Lauf. */
  hochladen: (dateien: File[], text?: string) => {
    const formular = new FormData()
    for (const d of dateien) formular.append('dateien', d)
    if (text && text.trim()) formular.append('text', text)
    return schickeFormular<NotierungLauf>(`${P}/laeufe/upload`, formular)
  },
  /** Eingefügter Mailtext (Pöhner-Angebot) als Lauf. */
  text: (text: string, dateiname?: string) =>
    hole<NotierungLauf>(`${P}/laeufe/text`, json('POST', { text, dateiname: dateiname ?? null })),
  /** Zuordnung einer Position ändern; `merken` legt eine Regel an (mit Spalte, sonst „nicht übernehmen“). */
  positionSetzen: (id: number, index: number, daten: { quelle_id: number | null; uebernehmen: boolean; merken?: boolean }) =>
    hole<NotierungLauf>(`${P}/laeufe/${id}/positionen/${index}`, json('PUT', { merken: false, ...daten })),
  uebernehmen: (id: number) => hole<NotierungLauf>(`${P}/laeufe/${id}/uebernehmen`, { method: 'POST' }),
  verwerfen: (id: number) => hole<NotierungLauf>(`${P}/laeufe/${id}/verwerfen`, { method: 'POST' }),
  zuruecknehmen: (id: number) => hole<NotierungLauf>(`${P}/laeufe/${id}/zuruecknehmen`, { method: 'POST' }),
  /** Handerfassung (Pöhner-Angebot am Telefon): wird gleich übernommen. */
  erfassen: (daten: {
    quelle_id: number
    notiert_am: string
    werte: { liefermonat: string; preis: number | null; status: NotierungStatus }[]
    bemerkung?: string | null
  }) => hole<NotierungLauf>(`${P}/erfassen`, json('POST', daten)),
  /** Altbestand aus einer der drei Excel-Mappen. */
  excel: (datei: File) => {
    const formular = new FormData()
    formular.append('datei', datei)
    return schickeFormular<NotierungExcelAntwort>(`${P}/excel`, formular)
  },

  // --- Stammdaten -------------------------------------------------------------------------------
  lieferanten: () => hole<NotierungLieferant[]>(`${P}/lieferanten`),
  lieferantAnlegen: (daten: { name: string; absender?: string | null; hinweis?: string | null }) =>
    hole<NotierungLieferant>(`${P}/lieferanten`, json('POST', daten)),
  lieferantAendern: (id: number, daten: Partial<Omit<NotierungLieferant, 'id' | 'quellen_anzahl'>>) =>
    hole<NotierungLieferant>(`${P}/lieferanten/${id}`, json('PUT', daten)),

  rohstoffe: () => hole<NotierungRohstoff[]>(`${P}/rohstoffe`),
  rohstoffAnlegen: (daten: Partial<Omit<NotierungRohstoff, 'id'>>) => hole<NotierungRohstoff>(`${P}/rohstoffe`, json('POST', daten)),
  rohstoffAendern: (id: number, daten: Partial<Omit<NotierungRohstoff, 'id'>>) =>
    hole<NotierungRohstoff>(`${P}/rohstoffe/${id}`, json('PUT', daten)),

  quellen: (nurAktive = false) => hole<NotierungQuelle[]>(mitSuche(`${P}/quellen`, { nur_aktive: nurAktive ? 'true' : undefined })),
  quelleAnlegen: (daten: NotierungQuelleEingabe) => hole<NotierungQuelle>(`${P}/quellen`, json('POST', daten)),
  quelleAendern: (id: number, daten: NotierungQuelleEingabe) => hole<NotierungQuelle>(`${P}/quellen/${id}`, json('PUT', daten)),
  /** 409, wenn schon Notierungen dranhängen - dann stattdessen deaktivieren. */
  quelleLoeschen: (id: number) => loesche(`${P}/quellen/${id}`),

  /** Zuordnungsregeln, sortiert nach Lieferant und Produkt. */
  regeln: (filter: { lieferant_id?: number; quelle_id?: number; nur_aktive?: boolean } = {}) =>
    hole<NotierungRegel[]>(
      mitSuche(`${P}/regeln`, { lieferant_id: filter.lieferant_id, quelle_id: filter.quelle_id, nur_aktive: filter.nur_aktive ? 'true' : undefined }),
    ),
  /**
   * Nach Anlegen und Ändern ordnet das Backend die wartenden Läufe neu zu (`neu_bewertet`). Fehlt die Spalte
   * oder gehört sie einem anderen Lieferanten, kommt ein Satz in `detail`; 409: dieselbe Regel gibt es schon.
   */
  regelAnlegen: (daten: NotierungRegelEingabe) => hole<NotierungRegelAntwort>(`${P}/regeln`, json('POST', daten)),
  regelAendern: (id: number, daten: Omit<NotierungRegelEingabe, 'herkunft'>) => hole<NotierungRegelAntwort>(`${P}/regeln/${id}`, json('PUT', daten)),
  regelLoeschen: (id: number) => loesche(`${P}/regeln/${id}`),

  einstellungen: () => hole<NotierungEinstellungen>(`${P}/einstellungen`),
  einstellungenSetzen: (daten: Partial<NotierungEinstellungen>) => hole<NotierungEinstellungen>(`${P}/einstellungen`, json('PUT', daten)),
}
